import React, { useEffect, useMemo, useState } from 'react';
import { content, BlogPost, SupportedLocale, localizeBlogPost } from '../lib/content';

type BlogPageProps = {
  locale?: SupportedLocale;
};

const categoriesOrder = [
  'Wszystkie',
  'Paragraf Zwierzoochronny',
'Franki, SKD, WIBOR',
  'Legalizacja pobytu',
'Poradnik klienta',
  
];

const monthMap: Record<string, number> = {
  stycznia: 0,
  lutego: 1,
  marca: 2,
  kwietnia: 3,
  maja: 4,
  czerwca: 5,
  lipca: 6,
  sierpnia: 7,
  września: 8,
  października: 9,
  listopada: 10,
  grudnia: 11,
};

const categoryLabels: Record<
  SupportedLocale,
  Record<string, string>
> = {
  pl: {
    Wszystkie: 'Wszystkie',
    'Paragraf Zwierzoochronny': 'Paragraf Zwierzoochronny',
    'Poradnik klienta': 'Poradnik klienta',
    'Legalizacja pobytu': 'Legalizacja pobytu',
    'Franki, SKD, WIBOR': 'Franki, SKD, WIBOR',
  },
  en: {
    Wszystkie: 'All',
    'Paragraf Zwierzoochronny': 'Animal rights',
    'Poradnik klienta': 'Client guide',
    'Legalizacja pobytu': 'Legalisation of stay',
    'Franki, SKD, WIBOR': 'Finance and loans',
  },
  es: {
    Wszystkie: 'Todas',
    'Paragraf Zwierzoochronny': 'Derechos de los animales',
    'Poradnik klienta': 'Guía para clientes',
    'Legalizacja pobytu': 'Legalización de la estancia',
    'Franki, SKD, WIBOR': 'Finanzas y créditos',
  },
};

const blogCopy: Record<
  SupportedLocale,
  {
    heroTitle: string;
    heroLead: string;
    heroMuted: string;
heroAuthor: string;
    filtersLabel: string;
    readLabel: string;
  }
> = {
  pl: {
    heroTitle: 'Blog',
    heroLead:
      'Praktyczne wskazówki z zakresu praw zwierząt, Franków, SKD, WIBOR oraz legalizacji pobytu w RP',
    heroMuted: 'Paragraf Zwierzoochronny – specjalny cykl tylko o prawach zwierząt',
heroAuthor: 'Autorem artykułów na blogu jest adwokat Piotr Pieńkowski',
    filtersLabel: 'Kategorie bloga',
    readLabel: 'Czytaj',
  },
  en: {
    heroTitle: 'Blog',
    heroLead:
      'Practical insights on animal rights, finance and legalisation of stay in Poland',
    heroMuted: '',
heroAuthor: 'All articles are authored by attorney-at-law Piotr Pieńkowski',
    filtersLabel: 'Blog categories',
    readLabel: 'Read',
  },
  es: {
    heroTitle: 'el Blog',
    heroLead:
      'Consejos prácticos sobre derechos de los animales, legalización de la estancia y cuestiones financieras',
    heroMuted: '',
heroAuthor: 'Los artículos del blog están escritos por el abogado Piotr Pieńkowski',
    filtersLabel: 'Categorías del blog',
    readLabel: 'Leer',
  },
};

const parseBlogDate = (dateStr: string): number => {
  const parts = dateStr.split(' ').filter(Boolean);
  if (parts.length < 3) return 0;
  const [dayStr, monthStr, yearStr] = parts;
  const day = parseInt(dayStr, 10);
  const month = monthMap[monthStr.toLowerCase()] ?? 0;
  const year = parseInt(yearStr, 10);
  if (Number.isNaN(day) || Number.isNaN(year)) return 0;
  return new Date(year, month, day).getTime();
};

const monthNamesEn = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const monthNamesEs = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const formatDate = (dateStr: string, locale: SupportedLocale): string => {
  if (locale === 'pl') return dateStr;
  const parts = dateStr.split(' ').filter(Boolean);
  if (parts.length < 3) return dateStr;
  const [dayStr, monthStr, yearStr] = parts;
  const day = parseInt(dayStr, 10);
  const monthIndex = monthMap[monthStr.toLowerCase()];
  const year = parseInt(yearStr, 10);
  if (
    Number.isNaN(day) ||
    Number.isNaN(year) ||
    monthIndex === undefined ||
    monthIndex < 0 ||
    monthIndex > 11
  ) {
    return dateStr;
  }
  if (locale === 'en') {
    return `${day} ${monthNamesEn[monthIndex]} ${year}`;
  }
  // es
  return `${day} de ${monthNamesEs[monthIndex]} de ${year}`;
};

const formatReadingTime = (readingTime: string, locale: SupportedLocale): string => {
  if (locale === 'pl') return readingTime;
  const match = readingTime.match(/(\d+)/);
  const minutes = match ? match[1] : '';
  if (!minutes) {
    return locale === 'en' ? 'Few minutes read' : 'Unos minutos de lectura';
  }
  if (locale === 'en') {
    return `${minutes} min read`;
  }
  // es
  return `${minutes} min de lectura`;
};

export const BlogPage: React.FC<BlogPageProps> = ({ locale = 'pl' }) => {
  const { posts } = content[locale].blog;
  const categories = categoriesOrder.filter(
    (cat) => cat === 'Wszystkie' || posts.some((p) => p.category === cat)
  );

  const [activeCategory, setActiveCategory] = useState<string>('Wszystkie');

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const fromHash = categories.find(
      (cat) => cat !== 'Wszystkie' && cat.toLowerCase().replace(/\s+/g, '-') === hash
    );
    if (fromHash) {
      setActiveCategory(fromHash);
    }
  }, [categories]);

  const sortedPosts = useMemo(
    () => [...posts].sort((a, b) => parseBlogDate(b.date) - parseBlogDate(a.date)),
    [posts]
  );

  const visiblePosts = useMemo(
    () =>
      activeCategory === 'Wszystkie'
        ? sortedPosts
        : sortedPosts.filter((post) => post.category === activeCategory),
    [sortedPosts, activeCategory]
  );

  const { heroTitle, heroLead, heroMuted, heroAuthor, filtersLabel, readLabel } = blogCopy[locale];

  const renderCard = (post: BlogPost) => (
    <article className="card blog-card blog-card--wide" key={post.slug}>
      {/*
        Localize title/teaser at render time so lists
        show EN/ES text if translations are provided.
      */}
      {(() => {
        const localized = localizeBlogPost(post, locale);
        return (
          <>
            <div className="blog-meta">
              <span className="badge">
                {categoryLabels[locale][post.category] ?? post.category}
              </span>
              <span className="muted blog-meta-detail">
                {formatDate(post.date, locale)} • {formatReadingTime(post.readingTime, locale)}
              </span>
            </div>
            <h2>{localized.title}</h2>
            <p>{localized.teaser}</p>
            <a className="inline-link with-icon" href={post.link}>
              {readLabel}
              <span aria-hidden="true">→</span>
            </a>
          </>
        );
      })()}
    </article>
  );

  return (
    <div className="blog-page">
      <section className="blog-hero">
        <div className="container blog-hero-inner">
          <div>
            <div className="eyebrow"></div>
            <h1>{heroTitle}</h1>
            <p className="lead">{heroLead}</p>
            <p className="muted">{heroMuted}</p>
<p className="muted">{heroAuthor}</p>
          </div>
        </div>
      </section>

      <section className="blog-filters">
        <div className="container">
          <div className="filter-pills" role="tablist" aria-label={filtersLabel}>
            {categories.map((cat) => {
              const isActive = cat === activeCategory;
              const hash = cat === 'Wszystkie' ? '' : `#${cat.toLowerCase().replace(/\s+/g, '-')}`;
              const label = categoryLabels[locale][cat] ?? cat;
              return (
              <a
                key={cat}
                className={`pill ${isActive ? 'is-active' : ''}`}
                href={`/blog${hash}`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveCategory(cat);
                  if (hash) {
                    window.history.replaceState(null, '', `/blog${hash}`);
                  } else {
                    window.history.replaceState(null, '', '/blog');
                  }
                }}
              >
                {label}
              </a>
            ); })}
          </div>
        </div>
      </section>

      <section className="blog-list">
        <div className="container blog-list-grid">
          {visiblePosts.map(renderCard)}
        </div>
      </section>
    </div>
  );
};
